import * as React from "react";
import {Alert, Button, Checkbox, FormGroup, Modal} from "react-bootstrap";
import {FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {ErrorDisplay} from "../common/ui/errordisplay/ErrorDisplay";
import {Spinner} from "../common/ui/spinner/Spinner";
import {mapErrorToMessage} from "../common/util/ErrorMapper";
import {BatchPropertyType, BatchUpdatePropertiesList, BatchUpdateProperty} from "./BatchUpdatePropertiesList";
import * as _ from "lodash";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {FormLabel} from "@material-ui/core";
import {DateUtil} from "../common/util/DateUtil";
import {LcdIcon} from "../common/ui/icon/LcdIcon";
import {CustomPropertiesDialog} from "./CustomPropertiesDialog";
import {BatchProperties, CustomPropertyDetail} from "./model";

export interface BatchUpdateDialogOwnProps {
  dataId: string;
  closeDialog: () => void;
}

type BatchUpdateDialogProps = BatchUpdateDialogOwnProps & InjectedIntlProps & WithApiProperties;

interface BatchUpdateDialogState {
  isLoading: boolean,
  isExecuting: boolean,
  isSuccess: boolean,
  error: Error,
  properties: BatchUpdateProperty[],
  applyToFolders: boolean,
  applyToImages: boolean,
  applyToVectors: boolean,
  applyToPointClouds: boolean,
  applyToPanoramics: boolean,
  applyToMeshes: boolean,
  applyToDocuments: boolean,
  directChildrenOnly: boolean,
  showCustomPropertiesDialog: boolean,
  selectedCustomProperties: CustomPropertyDetail[],
}

const CATEGORY_COMMON = "Common";
const CATEGORY_FOLDER = "Folder";
const CATEGORY_DATASET = "Dataset";
export const APPLICATION_APPEND = "Append";
export const APPLICATION_OVERWRITE = "Overwrite";

const BATCH_PROPERTIES: BatchUpdateProperty[] = [
  {
    name: "title",
    value: "",
    messageId: "studio.batch-update-properties-list.common-columns.title",
    defaultMessage: "Title",
    type: BatchPropertyType.STRING,
    category: CATEGORY_COMMON,
    applicationAppendable: false,
    application: APPLICATION_OVERWRITE
  },
  {
    name: "abstractText",
    value: "",
    messageId: "studio.batch-update-properties-list.common-columns.abstract",
    defaultMessage: "Abstract",
    type: BatchPropertyType.STRING,
    category: CATEGORY_COMMON,
    applicationAppendable: true,
    application: APPLICATION_OVERWRITE
  },
  {
    name: "keywords",
    value: "",
    messageId: "studio.batch-update-properties-list.common-columns.keywords",
    defaultMessage: "Keywords",
    type: BatchPropertyType.STRINGARRAY,
    category: CATEGORY_COMMON,
    applicationAppendable: true,
    application: APPLICATION_OVERWRITE
  }
];

class BatchUpdateDialogComponent extends React.Component<BatchUpdateDialogProps, BatchUpdateDialogState> {

  constructor(props: BatchUpdateDialogProps) {
    super(props);

    this.state = {
      isLoading: false,
      isExecuting: false,
      isSuccess: false,
      error: null,
      properties: _.cloneDeep(BATCH_PROPERTIES).map(p => {
        p.onChange = this.onPropertyChange;
        p.onToggleSelect = this.onPropertyToggleSelect;
        p.onApplicationChange = this.onPropertyChangeApplication;
        return p;
      }),
      applyToFolders: true,
      applyToImages: true,
      applyToVectors: true,
      applyToPointClouds: true,
      applyToPanoramics: true,
      applyToMeshes: true,
      applyToDocuments: true,
      directChildrenOnly: false,
      showCustomPropertiesDialog: false,
      selectedCustomProperties: [],
    };
  }

  openCustomPropertiesDialog = () => {
    this.setState({
      showCustomPropertiesDialog: true,
    });
  };

  closeCustomPropertiesDialog = () => {
    this.setState({
      showCustomPropertiesDialog: false,
    });
  };

  getNewBatchUpdateProperty = (propertyDetail: CustomPropertyDetail): BatchUpdateProperty => {
    let value: any = null;
    let type = null;

    switch (propertyDetail?.type) {
    case "Enum":
      value = propertyDetail?.possibleValues.length > 0 ? propertyDetail.possibleValues[0] : "";
      type = BatchPropertyType.ENUM;
      break;
    case "String":
      value = "";
      type = BatchPropertyType.STRING;
      break;
    case "Date":
      value = new Date();
      type = BatchPropertyType.DATE;
      break;
    case "Double":
      value = "0.0";
      type = BatchPropertyType.DOUBLE;
      break;
    case "Long":
      value = 0;
      type = BatchPropertyType.LONG;
      break;
    case "Boolean":
      value = false;
      type = BatchPropertyType.BOOL;
      break;
    }
    return {
      name: propertyDetail.name,
      value,
      messageId: `studio.batch-update-properties-list.common-columns.keywords${propertyDetail.name}`,
      defaultMessage: propertyDetail.displayName,
      type,
      possibleValues: propertyDetail.possibleValues ? propertyDetail.possibleValues : null,
      category: CATEGORY_DATASET,
      applicationAppendable: false,
      application: APPLICATION_OVERWRITE,
      onChange: this.onPropertyChange,
      onToggleSelect: this.onPropertyToggleSelect,
      onApplicationChange: this.onPropertyChangeApplication,
    } as BatchUpdateProperty;
  }

  updateSelectedCustomProperties = (selectedCustomProperties: CustomPropertyDetail[]) => {
    const {properties} = this.state;
    const newProperties = properties;
    const currentProperties = ["title", "abstractText", "keywords"].concat(
        selectedCustomProperties.map(prop => prop.name));
    const propertyList = properties.map(prop => prop.name);

    if (properties.length > currentProperties.length) {
      //delete
      const deleteProperty = propertyList.filter(property => !currentProperties.includes(property));
      const deleteIndex = newProperties.findIndex(
          property => property.name === (deleteProperty ? deleteProperty[0] : null));
      if (deleteIndex >= 0) {
        newProperties.splice(deleteIndex, 1);
      }
    } else {
      //add
      const addProperty = currentProperties.filter(property => !propertyList.includes(property));
      if (addProperty?.length > 0) {
        const propertyDetail = selectedCustomProperties.find(property => property.name === addProperty[0]);
        if (propertyDetail) {
          newProperties.push(this.getNewBatchUpdateProperty(propertyDetail));
        }
      }
    }
    this.setState({selectedCustomProperties, properties: newProperties});
  }

  getSelectedCustomProperties = (): CustomPropertyDetail[] => {
    return this.state.selectedCustomProperties;
  }

  renderTitle() {
    const {isExecuting, isSuccess} = this.state;

    if (isExecuting || isSuccess) {
      return null;
    }
    return <Modal.Title><FormattedMessage id="studio.data-batch-update-dialog.title"
                                          defaultMessage="Batch update"/></Modal.Title>;
  }

  renderButtons() {
    const {isExecuting, isSuccess} = this.state;

    if (isExecuting || isSuccess) {
      return null;
    }

    return (
        <div>
          <Button onClick={this.saveChanges}>
            <FormattedMessage id="studio.data-batch-update-dialog.update" defaultMessage="Update"/>
          </Button>
          <Button onClick={this.props.closeDialog}><FormattedMessage id="studio.ui.data-batch-update-dialog.cancel"
                                                                     defaultMessage="Cancel"/></Button>
        </div>
    );
  }

  renderMessage(message: React.ReactElement) {
    return (
        <div style={{textAlign: "center"}}>
          <h2>{message}</h2>
          <Spinner bigStyle/>
        </div>
    );
  }

  renderExecuting() {
    return this.renderMessage(<FormattedMessage id="studio.data-batch-update-dialog.updating"
                                                defaultMessage="Updating..."/>);
  }

  renderLoading() {
    return this.renderMessage(<FormattedMessage id="studio.data-batch-update-dialog.loading"
                                                defaultMessage="Loading..."/>);
  }

  renderError() {
    const {error} = this.state;

    if (error) {
      const errorMessage = mapErrorToMessage(error);
      if (errorMessage) {
        return <Alert bsStyle="danger">{errorMessage}</Alert>;
      }
      return <ErrorDisplay error={error}/>;
    }
    return null;
  }

  renderBody() {
    const {isLoading, isExecuting, properties, showCustomPropertiesDialog} = this.state;

    if (isLoading) {
      return this.renderLoading();
    }

    if (isExecuting) {
      return this.renderExecuting();
    }

    return (
        <div>
          {this.renderError()}
          <FormLabel>
            <FormattedMessage id="studio.data-batch-update-dialog.apply-to.label" defaultMessage="Apply To"/>
          </FormLabel>
          <FormGroup controlId="applyToGroup" className="inline-checkbox-row">
            <Checkbox inline name="applyToFolders"
                      checked={this.state.applyToFolders} onChange={this.onChange}>
              <FormattedMessage id="studio.data-batch-update-dialog.apply-to-folders.label"
                                defaultMessage="Folders"/>
            </Checkbox>
            <Checkbox inline name="applyToImages"
                      checked={this.state.applyToImages} onChange={this.onChange}>
              <FormattedMessage id="studio.data-batch-update-dialog.apply-to-images.label"
                                defaultMessage="Images"/>
            </Checkbox>
            <Checkbox inline name="applyToVectors"
                      checked={this.state.applyToVectors} onChange={this.onChange}>
              <FormattedMessage id="studio.data-batch-update-dialog.apply-to-vectors.label"
                                defaultMessage="Vectors"/>
            </Checkbox>
            <Checkbox inline name="applyToPointClouds"
                      checked={this.state.applyToPointClouds} onChange={this.onChange}>
              <FormattedMessage id="studio.data-batch-update-dialog.apply-to-point-clouds.label"
                                defaultMessage="Point Clouds"/>
            </Checkbox>
            <Checkbox inline name="applyToPanoramics"
                      checked={this.state.applyToPanoramics} onChange={this.onChange}>
              <FormattedMessage id="studio.data-batch-update-dialog.apply-to-panoramics.label"
                                defaultMessage="Panoramics"/>
            </Checkbox>
            <Checkbox inline name="applyToMeshes"
                      checked={this.state.applyToMeshes} onChange={this.onChange}>
              <FormattedMessage id="studio.data-batch-update-dialog.apply-to-meshes.label"
                                defaultMessage="Meshes"/>
            </Checkbox>
            <Checkbox inline name="applyToDocuments"
                      checked={this.state.applyToDocuments} onChange={this.onChange}>
              <FormattedMessage id="studio.data-batch-update-dialog.apply-to-documents.label"
                                defaultMessage="Documents"/>
            </Checkbox>
          </FormGroup>
          <Checkbox inline name="directChildrenOnly"
                    checked={this.state.directChildrenOnly} onChange={this.onChange}>
            <FormattedMessage id="studio.data-batch-update-dialog.direct-children-only.label"
                              defaultMessage="Direct Children Only"/>
          </Checkbox>

          <BatchUpdatePropertiesList data={_.cloneDeep(properties)}/>
          <Button onClick={this.openCustomPropertiesDialog}>
            <LcdIcon icon={"plus"}/>
            <FormattedMessage id="studio.files-list.add-button" defaultMessage="Add"/>
          </Button>
          {showCustomPropertiesDialog &&
           <CustomPropertiesDialog api={this.props.api} onClose={this.closeCustomPropertiesDialog}
                                   onUpdate={this.updateSelectedCustomProperties}
                                   getCurrentCustomProperties={() => {
                                     return []
                                   }}
                                   getSelectedCustomProperties={this.getSelectedCustomProperties}
           />}
        </div>
    );
  }

  private onChange = (event: any) => {
    const {name} = event.target;
    let {value} = event.target;
    value = event.target.type === 'checkbox' ? event.target.checked : value;
    this.setState({[name]: value} as any);
  };


  render() {
    const {closeDialog} = this.props;
    const {isExecuting} = this.state;

    return (
        <Modal show={true}
               backdrop={"static"}
               onHide={closeDialog}
               bsSize="large"
        >
          <Modal.Header closeButton={!isExecuting}>
            {this.renderTitle()}
          </Modal.Header>
          <Modal.Body>
            {this.renderBody()}
          </Modal.Body>
          <Modal.Footer>
            {this.renderButtons()}
          </Modal.Footer>
        </Modal>
    );
  }

  private onPropertyChange = (event: any) => {
    const {properties} = this.state;
    const {name} = event.target;
    let {value} = event.target;
    value = event.target.type === 'checkbox' ? event.target.checked : value;

    const newProperties = properties.map(p => {
      if (p.name === name) {
        if (p.type === BatchPropertyType.LONG) {
          value = parseInt(value, 10);
        }
        if (p.type === BatchPropertyType.DOUBLE) {
          value = parseFloat(value);
        }
        return {
          ...p,
          value,
        };
      }
      return p;
    });

    this.setState({
      properties: newProperties,
    });
  };

  private onPropertyToggleSelect = (event: any) => {
    const {properties} = this.state;
    const {name} = event.target;
    let {value} = event.target;
    value = event.target.type === 'checkbox' ? event.target.checked : value;

    const newProperties = properties.map(p => {
      if (p.name === name) {
        return {
          ...p,
          selected: value,
        };
      }
      return p;
    });

    this.setState({
      properties: newProperties,
    });
  };

  private onPropertyChangeApplication = (event: any) => {
    const {properties} = this.state;
    const {name} = event.target;
    let {value} = event.target;
    value = event.target.type === 'checkbox' ? event.target.checked : value;

    const newProperties = properties.map(p => {
      if (p.name === name) {
        return {
          ...p,
          application: value,
        };
      }
      return p;
    });

    this.setState({
      properties: newProperties,
    });
  };

  private saveChanges = () => {
    Promise.resolve()
        .then(() => {
          const {api, dataId} = this.props;
          const {
            properties, applyToFolders, applyToImages, applyToVectors,
            applyToPointClouds, applyToPanoramics, applyToMeshes, applyToDocuments, directChildrenOnly
          } = this.state;

          this.setState({isExecuting: true});

          const patchPropertiesList = ["title", "abstractText", "keywords"];
          const patchPropertiesOverwrite = _.chain(properties)
              .filter(
                  p => patchPropertiesList.includes(p.name) && p.selected && p.application === APPLICATION_OVERWRITE)
              .keyBy(p => p.name)
              .mapValues(p => this.getPropertyValue(p))
              .value() as any;
          const patchPropertiesAppend = _.chain(properties)
              .filter(p => patchPropertiesList.includes(p.name) && p.selected && p.application === APPLICATION_APPEND)
              .keyBy(p => p.name)
              .mapValues(p => this.getPropertyValue(p))
              .value() as any;
          const putProperties = _.chain(properties)
              .filter(p => !patchPropertiesList.includes(p.name) && p.selected)
              .keyBy(p => p.name)
              .mapValues(p => this.getPropertyValue(p))
              .value() as any;

          const batchProperties: BatchProperties = {
            patch: {
              ...patchPropertiesOverwrite,
              append: patchPropertiesAppend
            },
            propertiesPut: {...putProperties}
          };

          return api.batchUpdate(dataId, batchProperties, applyToFolders, applyToImages, applyToVectors,
              applyToPointClouds, applyToPanoramics, applyToMeshes, applyToDocuments, directChildrenOnly);
        })
        .catch((error) => {
          this.setState({isExecuting: false, error});
          throw error;
        })
        .then(() => {
          this.setState({isExecuting: false});
          this.props.closeDialog();
        });
  };

  private getPropertyValue(property: BatchUpdateProperty): string | boolean | string[] | number {
    if (property.type === BatchPropertyType.STRINGARRAY) {
      return (property.value as string).split(",");
    } else if (property.type === BatchPropertyType.DATE) {
      return DateUtil.formatDateTime(property.value as string);
    } else if (property.type === BatchPropertyType.DOUBLE) {
      return property.value as string;
    }
    return property.value;
  }
}

export const BatchUpdateDialog = WithApi(injectIntl(BatchUpdateDialogComponent));